// Import react elements
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";
import { AccountLastUpdatedContext } from "global/context/AccountLastUpdatedContext";

// Import API calls
import { getDashboardData } from "api/APIStrichCalls";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";

// Import components
import { Grid, GridItem } from "global/components/Grid";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import SubscriptionSummaryPanel from "pages/account/components/SubscriptionSummaryPanel";
import RadialProgress from "global/components/RadialProgress";
import LineChart from "global/components/LineChart";
import HelpButton from "global/components/HelpButton";

// Import utilities
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import css stylesheet
import "./DashboardPage.css";




// Fetch dashboard summary from API
export const loadDashboardData = async (token, setDashboardData, setLoading, setErrorMessage, days = 30) => {

    // Set loading state 
    setLoading(true);

    // Clear previous errors
    setErrorMessage("");

    // Call API
    const response = await getDashboardData(token, days);

    // Handle successful response
    if (response?.success && response?.results) {

        setDashboardData(response.results);

    } else {

        // Handle API error response
        setErrorMessage(response?.error_message || response?.error || "Unable to load dashboard data.");

    }

    // End loading state
    setLoading(false);

};





const Dashboard = () => {

    // Set title inside the page
    usePageTitle("Dashboard");

    const navigate = useNavigate();

    // Get the token from AuthContext
    const { token, authReady } = useContext(AuthContext);

    // Get the account data from UserContext
    const { userData, subscriptionLevels, subscriptionPackages } = useContext(UserContext);

    // Get the global account update markers
    const { lastUpdated } = useContext(AccountLastUpdatedContext);

    // Get the dashboard-related markers
    const ordersLastUpdated = lastUpdated?.orders_last_updated || null;
    const transactionsLastUpdated = lastUpdated?.transactions_last_updated || null;

    // Store the latest account marker values without forcing the initial load effect to depend on them
    const ordersLastUpdatedRef = useRef(null);
    const transactionsLastUpdatedRef = useRef(null);

    // Store the marker values that were current when the dashboard was last loaded
    const loadedOrdersLastUpdatedRef = useRef(null);
    const loadedTransactionsLastUpdatedRef = useRef(null);

    // Prevent overlapping automatic refreshes
    const autoRefreshInProgressRef = useRef(false);

    // Use the account currency when available
    const accountCurrency = userData?.currency || "GBP";

    // Store dashboard data
    const [dashboardData, setDashboardData] = useState(null);

    // Store the selected reporting period for dashboard activity and trends
    const [dashboardDays, setDashboardDays] = useState(30);

    // Track loading state
    const [loading, setLoading] = useState(true);

    // Track loading errors
    const [errorMessage, setErrorMessage] = useState("");



    // Format revenue values that are already returned in the account currency
    const formatAccountCurrency = (amount) => {

        // Return the amount formatted using the account currency
        return FormatCurrency(accountCurrency, amount);

    };



    // Format a minor-unit currency amount as whole currency units
    const formatWholeCurrency = (currency, amount) => {

        // Drop the minor currency units without rounding the displayed whole amount up
        const wholeMinorAmount = Math.trunc(Number(amount || 0) / 100) * 100;

        // Return the amount without a fixed .00 suffix
        return FormatCurrency(currency, wholeMinorAmount, true).replace(/\.00$/, "");

    };



    // Keep the latest dashboard marker values in refs for the initial load effect
    useEffect(() => {

        // Store latest order marker
        ordersLastUpdatedRef.current = ordersLastUpdated;

        // Store latest transaction marker
        transactionsLastUpdatedRef.current = transactionsLastUpdated;

    }, [ordersLastUpdated, transactionsLastUpdated]);



    // Load dashboard data when auth becomes available
    useEffect(() => {

        // Return early if auth is not ready or token is missing
        if (!authReady || !token) {

            return;

        }

        // Trigger data load
        loadDashboardData(token, setDashboardData, setLoading, setErrorMessage, dashboardDays).then(() => {

            // Store the marker values from the initial dashboard load
            loadedOrdersLastUpdatedRef.current = ordersLastUpdatedRef.current;
            loadedTransactionsLastUpdatedRef.current = transactionsLastUpdatedRef.current;

        });

    }, [token, authReady, dashboardDays]);

    // Refresh dashboard data when relevant global markers change
    useEffect(() => {

        // Only refresh when auth is ready
        if (!authReady || !token) { return; }

        // Ignore empty/default order markers
        const ordersMarkerChanged = ordersLastUpdated && ordersLastUpdated !== "0001-01-01 00:00:00" && loadedOrdersLastUpdatedRef.current && ordersLastUpdated !== loadedOrdersLastUpdatedRef.current;

        // Ignore empty/default transaction markers
        const transactionsMarkerChanged = transactionsLastUpdated && transactionsLastUpdated !== "0001-01-01 00:00:00" && loadedTransactionsLastUpdatedRef.current && transactionsLastUpdated !== loadedTransactionsLastUpdatedRef.current;

        // Stop if nothing relevant has changed
        if (!ordersMarkerChanged && !transactionsMarkerChanged) { return; }

        // Stop if an automatic refresh is already running
        if (autoRefreshInProgressRef.current) { return; }

        // Mark the automatic refresh as running
        autoRefreshInProgressRef.current = true;

        // Reload dashboard data quietly in the background
        loadDashboardData(token, setDashboardData, () => {}, () => {}, dashboardDays).then(() => {

            // Store the marker values from the refreshed dashboard load
            loadedOrdersLastUpdatedRef.current = ordersLastUpdated;
            loadedTransactionsLastUpdatedRef.current = transactionsLastUpdated;

        }).finally(() => {

            // Mark the automatic refresh as finished
            autoRefreshInProgressRef.current = false;

        });

    }, [authReady, token, ordersLastUpdated, transactionsLastUpdated, dashboardDays]);


    // Extract dashboard values with safe defaults where appropriate

    // Number of paid orders in the selected period and the preceding equivalent period
    const ordersPeriod = dashboardData?.orders_period || 0;
    const ordersPreviousPeriod = dashboardData?.orders_previous_period || 0;

    // Order count change compared with the preceding period
    const ordersChangeValue = dashboardData?.orders_change_value || 0;
    const ordersChangePercent = dashboardData?.orders_change_percent;

    // Order value in the selected period and the preceding equivalent period
    const revenuePeriod = dashboardData?.revenue_period || 0;
    const revenuePreviousPeriod = dashboardData?.revenue_previous_period || 0;

    // Order value change compared with the preceding period
    const revenueChangeValue = dashboardData?.revenue_change_value || 0;
    const revenueChangePercent = dashboardData?.revenue_change_percent;

    // POD design counts
    const podDesignsTotal = dashboardData?.designs?.pod_designs_total || 0;
    const podDesignsActive = dashboardData?.designs?.pod_designs_active || 0;
    const podDesignsInactive = dashboardData?.designs?.pod_designs_inactive || 0;

    // Calculate the proportion of designs which are active
    const podDesignsActiveProgress = podDesignsTotal > 0
        ? (Number(podDesignsActive) / Number(podDesignsTotal)) * 100
        : 0;

    // POD product counts
    const podProductsTotal = dashboardData?.products?.pod_products_total || 0;
    const podProductsActive = dashboardData?.products?.pod_products_active || 0;
    const podProductsInactive = dashboardData?.products?.pod_products_inactive || 0;

    // Calculate the proportion of products which are active
    const podProductsActiveProgress = podProductsTotal > 0
        ? (Number(podProductsActive) / Number(podProductsTotal)) * 100
        : 0;

    // Total unsettled account balance
    const balance = dashboardData?.balance || 0;

    // Available account balance
    const availableBalance = dashboardData?.available_balance || 0;

    // Account transaction totals for the selected period
    const transactionIncomePeriod = dashboardData?.transactions_period?.income || 0;
    const transactionExpensesPeriod = dashboardData?.transactions_period?.expenses || 0;
    const transactionTotalPeriod = dashboardData?.transactions_period?.total || 0;

    // Calculate the proportion of transaction movement which was income
    const transactionMovementPeriod = Number(transactionIncomePeriod) + Number(transactionExpensesPeriod);
    const transactionIncomeProgress = transactionMovementPeriod > 0
        ? (Number(transactionIncomePeriod) / transactionMovementPeriod) * 100
        : 0;

    // Current affiliate rolling value, rate target and nominal rate
    const affiliateRollingValue = Number(dashboardData?.affiliate?.rolling_commissionable_amount || 0);
    const affiliateRateTarget = Number(dashboardData?.affiliate?.rate_target_amount || 0);
    const affiliateRateCurrency = dashboardData?.affiliate?.rate_currency || accountCurrency;
    const affiliateNominalRate = Number(dashboardData?.affiliate?.nominal_rate || 0);

    // Calculate progress towards the affiliate rate target
    const affiliateTargetProgress = affiliateRateTarget > 0
        ? Math.max(0, Math.min(100, (affiliateRollingValue / affiliateRateTarget) * 100))
        : 0;

    // Daily history used by the dashboard chart
    const dashboardHistory = dashboardData?.history || [];

    // Define the series that can be switched on and off in the dashboard chart
    const dashboardHistorySeries = [
        { dataKey: "order_value", label: "Order Value", axisId: "currency", color: "var(--primary-color)" },
        { dataKey: "income", label: "Income", axisId: "currency", color: "#28a745" },
        { dataKey: "expenses", label: "Expenses", axisId: "currency", color: "var(--error-color)" },
        { dataKey: "orders", label: "Orders", axisId: "count", color: "#ff7f00" }
    ];

    // Define the Y axes used by the dashboard chart
    const dashboardHistoryAxes = [
        {
            id: "currency",
            position: "left",
            formatValue: (value) => formatAccountCurrency(value)
        },
        {
            id: "count",
            position: "right",
            integer: true,
            formatValue: (value) => Math.round(Number(value || 0)).toLocaleString("en-GB")
        }
    ];

    // Format an API date for chart labels and hover details
    const formatHistoryDate = (dateValue, fullDate = false) => {

        const dateParts = String(dateValue || "").split("-");

        // Return the original value when the API date is not in the expected format
        if (dateParts.length !== 3) { return dateValue; }

        const date = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));

        // Use a longer date in the hover detail
        if (fullDate) {
            return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
        }

        return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

    };



    // Determine order count change direction for styling
    const ordersChangeDirection = ordersChangeValue > 0 ? "positive" : ordersChangeValue < 0 ? "negative" : "neutral";

    // Determine whether to show the order count percentage change
    const showOrdersChange = ordersChangePercent !== null && ordersChangePercent !== undefined;

    // Format the signed order count change
    const ordersChangePrefix = ordersChangeValue > 0 ? "+" : ordersChangeValue < 0 ? "-" : "";
    const formattedOrdersChangeValue = `${ordersChangePrefix}${Math.abs(Number(ordersChangeValue)).toLocaleString("en-GB")}`;

    // Determine revenue change direction for styling
    const revenueChangeDirection = revenueChangeValue > 0 ? "positive" : revenueChangeValue < 0 ? "negative" : "neutral";

    // Determine whether to show revenue change
    const showRevenueChange = revenueChangePercent !== null && revenueChangePercent !== undefined;

    // Format the signed revenue change
    const revenueChangePrefix = revenueChangeValue > 0 ? "+" : revenueChangeValue < 0 ? "-" : "";
    const formattedRevenueChangeValue = `${revenueChangePrefix}${formatAccountCurrency(Math.abs(revenueChangeValue))}`;

    // Render loading state
    if (loading) {

        return (

            <Grid>

                <GridItem>

                    <div className="dashboard-status-panel">

                        <LoadingSpinner />
                        <p>Loading dashboard...</p>

                    </div>

                </GridItem>

            </Grid>

        );

    }



    // Render error state
    if (errorMessage) {

        return (

            <Grid>

                <GridItem>

                    <div className="dashboard-status-panel dashboard-status-panel--error">

                        <h3>Dashboard unavailable</h3>
                        <p>{errorMessage}</p>

                    </div>

                </GridItem>

            </Grid>

        );

    }



    // Render dashboard content
    return (

        <div className="dashboard-page">

            <section className="dashboard-section">

                <div className="dashboard-section__header">

                    <h2 className="dashboard-section__title">
                        Account <HelpButton section="dashboard" code="account" label="Account" />
                    </h2>

                    <div className="dashboard-chart-range">

                        <button
                            type="button"
                            className={`dashboard-chart-range__button ${dashboardDays === 30 ? "dashboard-chart-range__button--active" : ""}`}
                            onClick={() => setDashboardDays(30)}
                        >
                            30 Days
                        </button>

                        <button
                            type="button"
                            className={`dashboard-chart-range__button ${dashboardDays === 90 ? "dashboard-chart-range__button--active" : ""}`}
                            onClick={() => setDashboardDays(90)}
                        >
                            90 Days
                        </button>

                        <button
                            type="button"
                            className={`dashboard-chart-range__button ${dashboardDays === 365 ? "dashboard-chart-range__button--active" : ""}`}
                            onClick={() => setDashboardDays(365)}
                        >
                            365 Days
                        </button>

                    </div>

                </div>

                <Grid className="dashboard-section-grid dashboard-section-grid--account">

                    {/* Subscription Card */}
                    <GridItem className="dashboard-subscription-card cursor-pointer" onClick={() => navigate("/account")}>

                        <SubscriptionSummaryPanel
                            userData={userData}
                            subscriptionLevels={subscriptionLevels}
                            subscriptionPackages={subscriptionPackages}
                            buttonTitle="Upgrade"
                            buttonSize="m"
                            showDescription={false}
                            smallRenewDisplay={true}
                            showHelp={false}
                        />

                    </GridItem>

                    {/* Balance Card */}
                    <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/transactions")}>

                        <p className="dashboard-stat-card__label">Balance</p>
                        <p className="dashboard-stat-card__value">{FormatCurrency(accountCurrency, balance, true)}</p>
                        <p className="dashboard-stat-card__meta">Available: {FormatCurrency(accountCurrency, availableBalance, true)}</p>

                    </GridItem>

                </Grid>

            </section>

            <section className="dashboard-section">

                <h2 className="dashboard-section__title">
                    Last {dashboardDays} Days <HelpButton section="dashboard" code="last-30-days" label="Activity Summary" />
                </h2>

                <Grid className="dashboard-section-grid dashboard-section-grid--activity">

                    {/* Order Count Card */}
                    <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/orders")}>

                        <p className="dashboard-stat-card__label">Orders</p>

                        <p className="dashboard-stat-card__value">
                            {Number(ordersPeriod).toLocaleString("en-GB")}
                        </p>

                        <p className="dashboard-stat-card__change">
                            {formattedOrdersChangeValue}

                            {showOrdersChange && (
                                <span className={`dashboard-stat-card__change--${ordersChangeDirection}`}>
                                    {" "}({ordersChangePrefix}{Math.round(Number(ordersChangePercent))}%)
                                </span>
                            )}

                        </p>

                        <p className="dashboard-stat-card__meta">
                            Previous {dashboardDays} days: {Number(ordersPreviousPeriod).toLocaleString("en-GB")}
                        </p>

                    </GridItem>

                    {/* Order Value Card */}
                    <GridItem className="dashboard-stat-card">

                        <p className="dashboard-stat-card__label">Order Value</p>
                        <p className="dashboard-stat-card__value">{formatAccountCurrency(revenuePeriod)}</p>

                        <p className="dashboard-stat-card__change">
                            {formattedRevenueChangeValue}

                            {showRevenueChange && (
                                <span className={`dashboard-stat-card__change--${revenueChangeDirection}`}>
                                    {" "}({revenueChangePrefix}{Math.round(Number(revenueChangePercent))}%)
                                </span>
                            )}

                        </p>

                        <p className="dashboard-stat-card__meta">
                            Previous {dashboardDays} days: {formatAccountCurrency(revenuePreviousPeriod)}
                        </p>

                    </GridItem>

                    {/* Income Card */}
                    <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/transactions")}>
                       
                        <p className="dashboard-stat-card__label">Income</p>

                        <div className="dashboard-stat-card-label-and-radial">    

                            <div className="dashboard-stat-card__progress-summary">

                                <div className="dashboard-stat-card__progress-ring">

                                    <RadialProgress
                                        value={transactionIncomeProgress}
                                        direction="clockwise"
                                        size={108}
                                        strokeWidth={10}
                                        progressColor="var(--primary-color)"
                                        bgColor={transactionMovementPeriod > 0 ? "var(--error-color)" : "var(--input-border)"}
                                    />

                                </div>

                            </div>

                            <div className="dashboard-stat-card__progress-value">

                                <p className="dashboard-stat-card__value dashboard-stat-card__value--progress">
                                    {FormatCurrency(accountCurrency, transactionTotalPeriod, true)}
                                </p>

                                <p className="dashboard-stat-card__progress-target">
                                    {FormatCurrency(accountCurrency, transactionIncomePeriod, true)} income
                                </p>

                                <p className="dashboard-stat-card__progress-target dashboard-stat-card__progress-target--expense">
                                    {FormatCurrency(accountCurrency, transactionExpensesPeriod, true)} expenses
                                </p>

                            </div>

                        </div>

                    </GridItem>

                    {/* Affiliate Value Card */}
                    <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/affiliates")}>

                        <p className="dashboard-stat-card__label">Affiliate Rate</p>

                        <div className="dashboard-stat-card__progress-summary dashboard-stat-card-label-and-radial">

                            <div className="dashboard-stat-card__progress-ring">

                                <RadialProgress
                                    value={affiliateTargetProgress}
                                    direction="clockwise"
                                    size={108}
                                    strokeWidth={10}
                                    progressColor="var(--primary-color)"
                                    bgColor="var(--input-border)"
                                />

                                <span className="dashboard-stat-card__progress-text">
                                    {(affiliateNominalRate * 100).toFixed(2)}%
                                </span>

                            </div>

                            <div className="dashboard-stat-card__progress-value">

                                <p className="dashboard-stat-card__value dashboard-stat-card__value--progress">
                                    {formatWholeCurrency(affiliateRateCurrency, affiliateRollingValue)}
                                </p>

                                <p className="dashboard-stat-card__progress-target">
                                    of {formatWholeCurrency(affiliateRateCurrency, affiliateRateTarget)}
                                </p>

                            </div>

                        </div>

                    </GridItem>

                </Grid>

            </section>

            <section className="dashboard-section">

                <h2 className="dashboard-section__title">
                    Trends <HelpButton section="dashboard" code="trends" label="Trends" />
                </h2>

                <Grid className="dashboard-section-grid dashboard-section-grid--chart">

                    <GridItem className="dashboard-chart-card">

                        <div className="dashboard-chart-card__header">

                            <p className="dashboard-stat-card__label">Daily Activity</p>

                        </div>

                        <LineChart
                            data={dashboardHistory}
                            xKey="date"
                            series={dashboardHistorySeries}
                            axes={dashboardHistoryAxes}
                            formatXValue={formatHistoryDate}
                        />

                    </GridItem>

                </Grid>

            </section>

            <section className="dashboard-section">

                <h2 className="dashboard-section__title">
                    Create <HelpButton section="dashboard" code="create" label="Create" />
                </h2>

                <Grid className="dashboard-section-grid dashboard-section-grid--create">

                    {/* POD Designs Card */}
                    <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/designs")}>

                        <p className="dashboard-stat-card__label">Designs</p>

                        <div className="dashboard-stat-card__progress-summary">

                            <div className="dashboard-stat-card__progress-ring">

                                <RadialProgress
                                    value={podDesignsActiveProgress}
                                    direction="clockwise"
                                    size={108}
                                    strokeWidth={10}
                                    progressColor="var(--primary-color)"
                                    bgColor="var(--input-border)"
                                />

                                <span className="dashboard-stat-card__progress-text">
                                    {Math.round(podDesignsActiveProgress)}%
                                </span>

                            </div>

                            <div className="dashboard-stat-card__progress-value">

                                <p className="dashboard-stat-card__value dashboard-stat-card__value--progress">
                                    {Number(podDesignsTotal).toLocaleString("en-GB")}
                                </p>

                                <p className="dashboard-stat-card__progress-target">
                                    {Number(podDesignsActive).toLocaleString("en-GB")} active
                                </p>

                                <p className="dashboard-stat-card__progress-target">
                                    {Number(podDesignsInactive).toLocaleString("en-GB")} inactive
                                </p>

                            </div>

                        </div>

                    </GridItem>

                    {/* POD Products Card */}
                    <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/designs")}>

                        <p className="dashboard-stat-card__label">Products</p>

                        <div className="dashboard-stat-card__progress-summary">

                            <div className="dashboard-stat-card__progress-ring">

                                <RadialProgress
                                    value={podProductsActiveProgress}
                                    direction="clockwise"
                                    size={108}
                                    strokeWidth={10}
                                    progressColor="var(--primary-color)"
                                    bgColor="var(--input-border)"
                                />

                                <span className="dashboard-stat-card__progress-text">
                                    {Math.round(podProductsActiveProgress)}%
                                </span>

                            </div>

                            <div className="dashboard-stat-card__progress-value">

                                <p className="dashboard-stat-card__value dashboard-stat-card__value--progress">
                                    {Number(podProductsTotal).toLocaleString("en-GB")}
                                </p>

                                <p className="dashboard-stat-card__progress-target">
                                    {Number(podProductsActive).toLocaleString("en-GB")} active
                                </p>

                                <p className="dashboard-stat-card__progress-target">
                                    {Number(podProductsInactive).toLocaleString("en-GB")} inactive
                                </p>

                            </div>

                        </div>

                    </GridItem>

                </Grid>

            </section>

        </div>

    );

};

export default Dashboard;